/**
 * Paleta da empresa no Escritório (SPEC-20261011-1733). Cada imagem do Blender tem uma máscara do
 * mesmo tamanho: vermelho = paredes das salas, verde = estofados, azul = tapetes e carpetes,
 * branco = marca (recepção, marquise, anel do chão). A cor nova entra mantendo a luz e a sombra
 * da imagem original: troca o tom e a saturação, e a claridade só desliza para perto da cor nova.
 * Regras de bom gosto: parede fica sempre clara (tom suave da cor), estofado e tapete nunca viram
 * preto puro (preto é para detalhe), a marca usa a cor como ela é.
 */

export type Paleta = { paredes?: string | null; estofados?: string | null; tapetes?: string | null; marca?: string | null; cores?: string[] }
export type Papel = 'paredes' | 'estofados' | 'tapetes' | 'marca'
export const PAPEIS: Papel[] = ['paredes', 'estofados', 'tapetes', 'marca']

export const paletaVazia = (p: Paleta | null | undefined) => !p || PAPEIS.every((k) => !p[k])
export const chaveDaPaleta = (p: Paleta | null | undefined) => (paletaVazia(p) ? '' : PAPEIS.map((k) => p?.[k] ?? '-').join(','))

// ---------------------------------------------------------------- cor (OKLab) ---

const LIN = new Float32Array(256)
for (let i = 0; i < 256; i++) {
  const c = i / 255
  LIN[i] = c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
}
const SRGB = new Uint8ClampedArray(4097)
for (let i = 0; i <= 4096; i++) {
  const c = i / 4096
  SRGB[i] = Math.round((c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055) * 255)
}
const paraSrgb = (c: number) => SRGB[Math.round(Math.min(1, Math.max(0, c)) * 4096)] as number

type Lab = [number, number, number]
function lab(r: number, g: number, b: number): Lab {
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b)
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b)
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b)
  return [0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s, 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s, 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s]
}
function rgbLinear(L: number, a: number, b: number): [number, number, number] {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3
  return [4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s, -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s, -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s]
}
function labDoHex(hex: string): Lab {
  const n = parseInt(hex.slice(1), 16)
  return lab(LIN[(n >> 16) & 255] as number, LIN[(n >> 8) & 255] as number, LIN[n & 255] as number)
}

/** A cor que cada papel realmente usa (as regras de bom gosto). */
export function corDoPapel(papel: Papel | 'livre', hex: string): Lab {
  const [L, a, b] = labDoHex(hex)
  const C = Math.hypot(a, b)
  const h = Math.atan2(b, a)
  let L2 = L
  let C2 = C
  if (papel === 'livre') {
    // a cor como a pessoa escolheu, só sem os extremos (o branco puro estoura, o preto apaga a luz)
    L2 = Math.min(0.96, Math.max(0.16, L))
  } else if (papel === 'paredes') {
    L2 = Math.max(0.9, L * 0.25 + 0.72)
    C2 = Math.min(C * 0.32, 0.05)
  } else if (papel === 'estofados') {
    L2 = Math.min(0.82, Math.max(0.36, L))
    C2 = C * 0.88
  } else if (papel === 'tapetes') {
    // chão é área grande: um pouco mais fechado e menos saturado que a cor pura
    L2 = Math.min(0.62, Math.max(0.34, L * 0.92))
    C2 = C * 0.62
  } else {
    L2 = Math.min(0.88, Math.max(0.24, L))
  }
  return [L2, Math.cos(h) * C2, Math.sin(h) * C2]
}

/** Cor da prévia (hex) de um papel, para os quadradinhos do painel. */
export function hexDoPapel(papel: Papel, hex: string) {
  const [L, a, b] = corDoPapel(papel, hex)
  const [r, g, bl] = rgbLinear(L, a, b)
  return `#${[r, g, bl].map((c) => paraSrgb(c).toString(16).padStart(2, '0')).join('')}`
}

// ---------------------------------------------------------------- recolorir ---

const imagens = new Map<string, Promise<HTMLImageElement | null>>()
function carregar(src: string) {
  let p = imagens.get(src)
  if (!p) {
    p = new Promise((ok) => {
      const img = new Image()
      img.decoding = 'async'
      img.onload = () => ok(img)
      img.onerror = () => ok(null)
      img.src = src
    })
    imagens.set(src, p)
  }
  return p
}

const prontas = new Map<string, Promise<CanvasImageSource>>()

/**
 * A imagem com as cores da empresa. Sem paleta, sem máscara ou se algo falhar, devolve a original.
 * O resultado fica guardado por imagem + paleta.
 */
export function recolorida(img: HTMLImageElement, mascara: string, paleta: Paleta | null | undefined): Promise<CanvasImageSource> {
  const chave = chaveDaPaleta(paleta)
  if (!chave) return Promise.resolve(img)
  const id = `${img.src}|${chave}`
  let p = prontas.get(id)
  if (!p) {
    p = carregar(mascara).then((m) => (m ? pintar(img, m, paleta as Paleta) : img)).catch(() => img)
    prontas.set(id, p)
  }
  return p
}

function pintar(img: HTMLImageElement, mascara: HTMLImageElement, paleta: Paleta): CanvasImageSource {
  const w = img.naturalWidth
  const h = img.naturalHeight
  if (mascara.naturalWidth !== w || mascara.naturalHeight !== h) return img
  const tela = document.createElement('canvas')
  tela.width = w
  tela.height = h
  const g = tela.getContext('2d', { willReadFrequently: true })
  if (!g) return img
  g.drawImage(mascara, 0, 0)
  const mk = g.getImageData(0, 0, w, h).data
  g.clearRect(0, 0, w, h)
  g.drawImage(img, 0, 0)
  const dados = g.getImageData(0, 0, w, h)
  const px = dados.data
  // papel de cada canal: 0 paredes (R), 1 estofados (G), 2 tapetes (B), 3 marca (branco)
  const alvo: (Lab | null)[] = [paleta.paredes, paleta.estofados, paleta.tapetes, paleta.marca].map((c, i) => (c ? corDoPapel(PAPEIS[i] as Papel, c) : null))
  // claridade média de cada papel nesta imagem: a sombra e a luz ficam como diferença dela
  const soma = [0, 0, 0, 0]
  const peso = [0, 0, 0, 0]
  const pesos = (i: number, out: number[]) => {
    const r = (mk[i] as number) / 255
    const gg = (mk[i + 1] as number) / 255
    const b = (mk[i + 2] as number) / 255
    const branco = Math.min(r, gg, b)
    out[0] = r - branco
    out[1] = gg - branco
    out[2] = b - branco
    out[3] = branco
  }
  const wk = [0, 0, 0, 0]
  const n = w * h * 4
  for (let i = 0; i < n; i += 4) {
    if ((px[i + 3] as number) < 8 || ((mk[i] as number) | (mk[i + 1] as number) | (mk[i + 2] as number)) < 6) continue
    pesos(i, wk)
    const L = lab(LIN[px[i] as number] as number, LIN[px[i + 1] as number] as number, LIN[px[i + 2] as number] as number)[0]
    for (let k = 0; k < 4; k++) {
      const q = wk[k] as number
      if (q > 0.5 && alvo[k]) {
        soma[k] = (soma[k] as number) + L * q
        peso[k] = (peso[k] as number) + q
      }
    }
  }
  const media = soma.map((s, k) => (peso[k] ? s / (peso[k] as number) : 0))
  for (let i = 0; i < n; i += 4) {
    if ((px[i + 3] as number) === 0 || ((mk[i] as number) | (mk[i + 1] as number) | (mk[i + 2] as number)) < 6) continue
    pesos(i, wk)
    const r0 = LIN[px[i] as number] as number
    const g0 = LIN[px[i + 1] as number] as number
    const b0 = LIN[px[i + 2] as number] as number
    const [L] = lab(r0, g0, b0)
    let r = r0
    let gg = g0
    let b = b0
    for (let k = 0; k < 4; k++) {
      const q = Math.min(1, wk[k] as number)
      const t = alvo[k]
      if (q <= 0.01 || !t) continue
      // a luz da imagem continua: o que era mais escuro que a média segue mais escuro
      const L2 = Math.min(0.99, Math.max(0.02, t[0] + (L - (media[k] as number)) * 0.9))
      const f = Math.min(1.25, Math.max(0.55, L2 / Math.max(0.05, t[0])))
      const [nr, ng, nb] = rgbLinear(L2, t[1] * f, t[2] * f)
      r += (nr - r) * q
      gg += (ng - gg) * q
      b += (nb - b) * q
    }
    px[i] = paraSrgb(r)
    px[i + 1] = paraSrgb(gg)
    px[i + 2] = paraSrgb(b)
  }
  g.putImageData(dados, 0, 0)
  return tela
}

// ---------------------------------------------------------------- logo ---

export type LugarDaLogo = { tipo: string; x: number; y: number; w: number; h: number }

/** Desenha a logo da empresa nos lugares marcados (frente da recepção e anel do chão). */
export function desenharLogo(g: CanvasRenderingContext2D, logo: HTMLImageElement, lugares: LugarDaLogo[], dx = 0, dy = 0) {
  const lw = logo.naturalWidth || 1
  const lh = logo.naturalHeight || 1
  for (const l of lugares) {
    if (l.tipo === 'marquise') continue // fina demais para uma logo
    const chao = l.tipo === 'chao'
    const caixaW = l.w * (chao ? 0.66 : 0.8)
    const caixaH = l.h * (chao ? 0.66 : 0.72)
    // no chão a logo fica deitada: achatada na vertical como o anel
    const achata = chao ? l.h / Math.max(1, l.w) : 1
    const k = Math.min(caixaW / lw, caixaH / (lh * achata))
    const w = lw * k
    const h = lh * k * achata
    g.save()
    g.globalAlpha = chao ? 0.9 : 1
    g.drawImage(logo, dx + l.x + (l.w - w) / 2, dy + l.y + (l.h - h) / 2, w, h)
    g.restore()
  }
}

export const carregarLogo = (src: string) => carregar(src)

/* ------------------------------------------- pintar uma peça só (construção) --- */

const tingidas = new Map<string, Promise<CanvasImageSource>>()

/**
 * Pinta uma peça do kit da construção (SPEC-20261011-2023) numa cor, mantendo luz e sombra.
 * Com máscara, só onde ela é vermelha (a parede, não o vidro nem o batente); sem máscara, a peça toda.
 */
export function tingida(img: HTMLImageElement, mascara: string | null, hex: string, papel: Papel | 'livre'): Promise<CanvasImageSource> {
  const id = `${img.src}|${mascara ?? ''}|${hex}|${papel}`
  let p = tingidas.get(id)
  if (!p) {
    p = (mascara ? carregar(mascara) : Promise.resolve(null))
      .then((m) => tingir(img, m, corDoPapel(papel, hex)))
      .catch(() => img)
    tingidas.set(id, p)
  }
  return p
}

function tingir(img: HTMLImageElement, mascara: HTMLImageElement | null, t: Lab): CanvasImageSource {
  const w = img.naturalWidth
  const h = img.naturalHeight
  if (mascara && (mascara.naturalWidth !== w || mascara.naturalHeight !== h)) return img
  const tela = document.createElement('canvas')
  tela.width = w
  tela.height = h
  const g = tela.getContext('2d', { willReadFrequently: true })
  if (!g) return img
  let mk: Uint8ClampedArray | null = null
  if (mascara) {
    g.drawImage(mascara, 0, 0)
    mk = g.getImageData(0, 0, w, h).data
    g.clearRect(0, 0, w, h)
  }
  g.drawImage(img, 0, 0)
  const dados = g.getImageData(0, 0, w, h)
  const px = dados.data
  const n = w * h * 4
  const peso = (i: number) => ((px[i + 3] as number) < 8 ? 0 : mk ? (mk[i] as number) / 255 : 1)
  let soma = 0
  let total = 0
  for (let i = 0; i < n; i += 16) {
    const q = peso(i)
    if (q < 0.5) continue
    soma += lab(LIN[px[i] as number] as number, LIN[px[i + 1] as number] as number, LIN[px[i + 2] as number] as number)[0] * q
    total += q
  }
  const media = total ? soma / total : t[0]
  for (let i = 0; i < n; i += 4) {
    const q = Math.min(1, peso(i))
    if (q <= 0.01) continue
    const r0 = LIN[px[i] as number] as number
    const g0 = LIN[px[i + 1] as number] as number
    const b0 = LIN[px[i + 2] as number] as number
    const [L] = lab(r0, g0, b0)
    const L2 = Math.min(0.99, Math.max(0.02, t[0] + (L - media) * 0.9))
    const f = Math.min(1.25, Math.max(0.55, L2 / Math.max(0.05, t[0])))
    const [nr, ng, nb] = rgbLinear(L2, t[1] * f, t[2] * f)
    px[i] = paraSrgb(r0 + (nr - r0) * q)
    px[i + 1] = paraSrgb(g0 + (ng - g0) * q)
    px[i + 2] = paraSrgb(b0 + (nb - b0) * q)
  }
  g.putImageData(dados, 0, 0)
  return tela
}
